import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FlexColumn,
  Flex,
  DropDown,
  FlexCenter,
} from '@elementary/common-ui/components';
import DateRangePicker, {
  DateRangeValue,
} from '@elementary/common-ui/components/DateRangePicker';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import {
  DATA_POINTS,
  DIMENSION_VALUES,
} from '@elementary/common-ui/constants/queryKeys';
import dayjs from 'dayjs';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { radius } from '@elementary/common-ui/style/utils';
import * as api from '../api';
import { Metric } from '../types';
import MetricChart, {
  DataPoint as ChartDataPoint,
  LinePalette,
  LINES_PALETTE,
} from './MetricChart';

const Circle = styled(FlexCenter)<{ color?: string }>`
  width: 10px;
  height: 10px;
  min-width: 10px;
  min-height: 10px;
  border-radius: ${radius('round')};
  align-items: center;
  background: ${({ color }) => color || LINES_PALETTE[0].primary};
`;

const MetricExpandedRow = ({ metric }: { metric: Metric }) => {
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    start: dayjs().subtract(14, 'days').startOf('day'),
    end: dayjs(),
  });
  const debouncedDateRange = useDebounce(dateRange);

  const [dimensionValue, setDimensionValue] = useState<string>();

  const selectedEnvId = useSelectedEnv();

  const { data: dataPointsData, isFetching: dataPointsFetching } = useQuery(
    [
      DATA_POINTS,
      selectedEnvId,
      metric.database_name,
      metric.schema_name,
      metric.table_name,
      metric.column_name,
      metric.name,
      metric.source,
      debouncedDateRange.start?.format('YYYY-MM-DD hh:mm'),
      debouncedDateRange.end?.format('YYYY-MM-DD hh:mm'),
      dimensionValue,
    ],
    () =>
      api.fetchDataPoints(
        selectedEnvId!,
        metric.name,
        metric.database_name,
        metric.schema_name,
        metric.table_name,
        metric.source,
        debouncedDateRange.start!,
        debouncedDateRange.end!,
        metric.column_name,
        metric.group_by_dimensions.length > 0 ? dimensionValue : undefined,
      ),
    {
      enabled:
        debouncedDateRange.start != null &&
        debouncedDateRange.end != null &&
        (metric.group_by_dimensions.length === 0 || dimensionValue != null),
    },
  );

  const { data: dimensionValues, isFetching: dimensionValuesFetching } =
    useQuery(
      [DIMENSION_VALUES, selectedEnvId, metric.id],
      () =>
        api.fetchMetricDimensions(
          selectedEnvId!,
          metric.name,
          metric.database_name,
          metric.schema_name,
          metric.table_name,
          metric.source,
          metric.column_name,
        ),
      {
        enabled: metric.group_by_dimensions.length > 0,
        onSuccess: (data) => {
          if (data.length > 0) {
            setDimensionValue(data?.sort()[0]);
          }
        },
      },
    );

  const paletteByDimensionValue = useMemo(
    () =>
      dimensionValues?.sort().reduce((acc, val, index) => {
        acc[val] = LINES_PALETTE[index % LINES_PALETTE.length];
        return acc;
      }, {} as { [key: string]: LinePalette }),

    [dimensionValues],
  );

  const dataPoints = useMemo(
    () =>
      (dataPointsData || []).map(
        (val) =>
          ({
            value: val.metric_value,
            startTime: val.bucket_start,
            endTime: val.bucket_end,
            dimensionValue: val.dimension_value,
            metricName: val.metric_name,
          } as ChartDataPoint),
      ),
    [dataPointsData],
  );

  const dimensionValueOptions = useMemo(
    () =>
      (dimensionValues || []).map((dimension) => ({
        label: dimension,
        value: dimension,
        icon: <Circle color={paletteByDimensionValue?.[dimension].primary} />,
      })),
    [dimensionValues, paletteByDimensionValue],
  );

  useEffect(() => {
    if (dimensionValues && dimensionValues.length > 0) {
      setDimensionValue(dimensionValues.sort()[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FlexColumn gap={1}>
      <Flex
        sx={{
          width: '100%',
          flexDirection: 'row-reverse',
          justifyContent: 'space-between',
        }}
      >
        <DateRangePicker
          onChange={(val) => {
            setDateRange(val);
          }}
          value={dateRange}
          disableFuture
        />
        {metric.group_by_dimensions.length > 0 && (
          <DropDown
            sx={{ flex: 1, maxWidth: '300px' }}
            size="large"
            options={dimensionValueOptions}
            disabled={dimensionValuesFetching}
            value={dimensionValue}
            onChange={setDimensionValue}
            defaultValue={dataPoints?.[0]?.dimensionValue}
          />
        )}
      </Flex>
      <MetricChart
        dataPoints={dataPoints || []}
        colorPalette={
          metric.group_by_dimensions.length > 0 &&
          (dataPoints || []).length > 0 &&
          dataPoints?.[0]?.dimensionValue
            ? paletteByDimensionValue?.[
                dimensionValue || dataPoints?.[0].dimensionValue
              ]
            : undefined
        }
        loading={
          dataPointsFetching ||
          (metric.group_by_dimensions.length > 0 && dimensionValuesFetching)
        }
        source={metric.source}
      />
    </FlexColumn>
  );
};

export default MetricExpandedRow;
